/**
 * v1.3.18 — Focus Mode Queue Pill (Phase 2B).
 * v1.3.21 — Extended (Phase 3b): the pill now surfaces the SUM of
 *           pending AI jobs + pending field edits so a single glance
 *           tells the assessor everything they owe the network on
 *           reconnect. Tooltip breaks down the two counts.
 *
 * Compact status pill that lives in the sidebar next to the Focus
 * Mode toggle. Displays the total count of pending queued work and
 * opens the queue drawer on click. Renders nothing when the count
 * is 0 AND the feature flag is off — never visual noise for
 * tenants that haven't opted in.
 */
import React, { useEffect, useState } from 'react';
import { ListChecks } from 'lucide-react';
import {
  getPendingDeferredCount,
  subscribeDeferredJobs,
} from '../services/deferredAiJobsQueue';
// v1.3.21 — field-edit queue count folded into the same pill.
import {
  getPendingFieldEditsCount,
  subscribeFieldEdits,
} from '../services/offlineFieldEditQueue';
import { MANUAL_AIRPLANE_ENABLED } from '../lib/connectivity';
import FocusModeQueueDrawer from './FocusModeQueueDrawer';

export const FocusModeQueuePill = ({ collapsed = false }) => {
  const [aiCount, setAiCount] = useState(0);
  const [editCount, setEditCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const [n, m] = await Promise.all([
          getPendingDeferredCount(),
          getPendingFieldEditsCount(),
        ]);
        if (alive) { setAiCount(n); setEditCount(m); }
      } catch (_) { /* IndexedDB unavailable — hide pill */ }
    };
    refresh();
    const unsub1 = subscribeDeferredJobs(refresh);
    const unsub2 = subscribeFieldEdits(refresh);
    // Light polling as a fallback for cross-tab writes (subscribe is
    // in-tab only).
    const id = setInterval(refresh, 6000);
    return () => { alive = false; unsub1(); unsub2(); clearInterval(id); };
  }, []);

  if (!MANUAL_AIRPLANE_ENABLED) return null;
  const count = aiCount + editCount;
  if (count === 0) return null;

  const tip = (() => {
    const parts = [];
    if (aiCount > 0) parts.push(`${aiCount} AI task${aiCount > 1 ? 's' : ''}`);
    if (editCount > 0) parts.push(`${editCount} field edit${editCount > 1 ? 's' : ''}`);
    return `${parts.join(' + ')} queued — tap to review`;
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        data-testid="focus-mode-queue-pill"
        title={tip}
        className={
          'mb-2 flex items-center gap-1.5 rounded-md transition-colors ' +
          (collapsed
            ? 'md:justify-center h-8 w-8 mx-auto bg-sky-500/20 border border-sky-400/40 text-sky-100'
            : 'w-full px-2 py-1 bg-sky-500/15 border border-sky-400/30 text-sky-100 hover:bg-sky-500/25')
        }
      >
        <ListChecks size={12} className="flex-shrink-0" />
        {collapsed ? (
          <span className="text-[10px] font-bold">{count}</span>
        ) : (
          <span className="text-[11px] font-medium tracking-tight flex-1 min-w-0 truncate text-left">
            {count} queued
          </span>
        )}
      </button>
      <FocusModeQueueDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
};

export default FocusModeQueuePill;
